import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  geocodeLocation,
  geocodeLocationSuggestions,
  GeocodingSuggestion,
} from "./utils/geocoding";
import { fetchSunriseSunsetData, DataSource } from "./utils/sunriseSunsetApi";
import { usePlanetaryHour } from "./hooks/usePlanetaryHour";
import { useDefaultLocations } from "./hooks/useDefaultLocations";
import DefaultLocations from "./components/DefaultLocations";
import LocationInput from "./components/LocationInput";
import DateSelector from "./components/DateSelector";
import SunriseResults from "./components/SunriseResults";
import { GulikaCalculator } from "./utils/gulikaCalculator";
import { DateTime } from "luxon";
import {
  calculateBrahmaMuhurta,
  calculateAbhijitMuhurta,
  Muhurta,
} from "./utils/muhurtasCalculator";
import {
  saveLocation,
  restoreLocation,
  SavedLocation,
} from "./utils/locationStorage";
import {
  addCustomShortcut,
  canAddMoreShortcuts,
  getCustomShortcuts,
  CustomShortcut,
} from "./utils/customShortcutsStorage";

const SunriseSunset: React.FC = () => {
  const [location, setLocation] = useState("");
  const [suggestions, setSuggestions] = useState<GeocodingSuggestion[]>([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [submittedLocation, setSubmittedLocation] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [sunrise, setSunrise] = useState<Date | null>(null);
  const [sunset, setSunset] = useState<Date | null>(null);
  const [solarNoon, setSolarNoon] = useState<Date | null>(null);
  const [gulikaInterval, setGulikaInterval] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [rahuInterval, setRahuInterval] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [yamagandamInterval, setYamagandamInterval] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [brahmaMuhurta, setBrahmaMuhurta] = useState<Muhurta | null>(null);
  const [abhijitMuhurta, setAbhijitMuhurta] = useState<Muhurta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSource] = useState<DataSource>("tripura"); // Default to Tripura API
  // Add timezone state - default to browser's timezone
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const initialLoadDone = useRef(false);
  const lastFetchRef = useRef<{
    lat: number;
    lng: number;
    date: string;
  } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Flag to detect user-initiated fetch (button or Enter)
  const userRequestedRef = useRef(false);

  const {
    defaultLocations,
    customShortcuts,
    handleRemoveCustomShortcut,
    refreshCustomShortcuts,
    initialized,
  } = useDefaultLocations();
  const [showSaveShortcutButton, setShowSaveShortcutButton] = useState(false);
  const [isUserInitiatedSearch, setIsUserInitiatedSearch] = useState(false);

  // Memoize the default location name
  const defaultLocationName = useMemo(
    () => defaultLocations[0]?.name || "București",
    [defaultLocations],
  );

  // Use the usePlanetaryHour hook with the selected date
  // The hook and components will handle the after-midnight case internally
  const { currentPlanetaryHour, countdown, progress } = usePlanetaryHour(
    sunrise,
    sunset,
  );

  // Calculate intervals whenever sunrise or sunset changes
  useEffect(() => {
    if (sunrise && sunset && solarNoon) {
      const calculator = new GulikaCalculator();
      const date = DateTime.fromJSDate(selectedDate);
      const dayTimes = {
        sunrise: DateTime.fromJSDate(sunrise),
        sunset: DateTime.fromJSDate(sunset),
      };

      // Calculate intervals for the selected date

      try {
        // Calculate Gulika interval
        const gulikaInt = calculator.calculateGulikaInterval(date, dayTimes);
        setGulikaInterval({
          start: gulikaInt.start.toJSDate(),
          end: gulikaInt.end.toJSDate(),
        });

        // Calculate Rahu interval
        const rahuInt = calculator.calculateRahuInterval(date, dayTimes);
        setRahuInterval({
          start: rahuInt.start.toJSDate(),
          end: rahuInt.end.toJSDate(),
        });

        // Calculate Yamagandam interval
        const yamagandamInt = calculator.calculateYamagandamInterval(
          date,
          dayTimes,
        );
        setYamagandamInterval({
          start: yamagandamInt.start.toJSDate(),
          end: yamagandamInt.end.toJSDate(),
        });

        // Calculate Brahma Muhurta
        try {
          const brahmaMuhurtaObj = calculateBrahmaMuhurta(sunrise, sunset);
          setBrahmaMuhurta(brahmaMuhurtaObj);
        } catch (error) {
          console.error("Error calculating Brahma Muhurta:", error);
          setBrahmaMuhurta(null);
        }

        // Calculate Abhijit Muhurta
        try {
          const abhijitMuhurtaObj = calculateAbhijitMuhurta(solarNoon);
          setAbhijitMuhurta(abhijitMuhurtaObj);
        } catch (error) {
          console.error("Error calculating Abhijit Muhurta:", error);
          setAbhijitMuhurta(null);
        }

        // All intervals calculated successfully
      } catch (error) {
        console.error("Error calculating intervals:", error);
        setGulikaInterval(null);
        setRahuInterval(null);
        setYamagandamInterval(null);
        setBrahmaMuhurta(null);
        setAbhijitMuhurta(null);
      }
    } else {
      setGulikaInterval(null);
      setRahuInterval(null);
      setYamagandamInterval(null);
      setBrahmaMuhurta(null);
      setAbhijitMuhurta(null);
    }
  }, [sunrise, sunset, solarNoon, selectedDate]);

  // Restore saved location on component initialization
  useEffect(() => {
    if (initialized && !initialLoadDone.current) {
      const savedLocation = restoreLocation();
      if (savedLocation) {
        // Restore the saved location data
        setLocation(savedLocation.location);
        setSubmittedLocation(savedLocation.submittedLocation);

        // Fetch sunrise/sunset data for the saved location
        initialLoadDone.current = true;
        fetchSavedLocationData(savedLocation);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  // Fetch data for default location on initial load (only if no saved location)
  useEffect(() => {
    if (
      initialized &&
      defaultLocations.length > 0 &&
      !initialLoadDone.current
    ) {
      initialLoadDone.current = true; // Mark that we've attempted to load data
      fetchDefaultLocationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, defaultLocations]);

  // Fetch data when date changes (if we already have a location)
  useEffect(() => {
    // Cancel any in-progress requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Only fetch if we've already loaded data once and we're not currently loading
    if (initialLoadDone.current && !loading && sunrise && sunset) {
      fetchSunriseSunset();
    }

    return () => {
      // Clean up any pending requests when unmounting or when date changes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Function to check if we've already fetched data for this location and date
  const shouldSkipFetch = useCallback(
    (lat: number, lng: number, date: Date): boolean => {
      if (!lastFetchRef.current) return false;

      const dateString = date.toDateString();

      // Use a slightly more lenient comparison for all locations
      return (
        Math.abs(lastFetchRef.current.lat - lat) < 0.001 &&
        Math.abs(lastFetchRef.current.lng - lng) < 0.001 &&
        lastFetchRef.current.date === dateString
      );
    },
    [],
  );

  const fetchSavedLocationData = async (savedLocation: SavedLocation) => {
    if (loading) return; // Avoid duplicate requests

    // Cancel any in-progress fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const { lat, lng } = savedLocation;

      // Skip if we've already fetched this data
      if (shouldSkipFetch(lat, lng, selectedDate)) {
        setLoading(false);
        return;
      }

      const sunriseData = await fetchSunriseSunsetData(
        lat,
        lng,
        selectedDate,
        dataSource,
      );

      // Check if request was aborted or component unmounted
      if (abortControllerRef.current === null) return;

      if (sunriseData) {
        setSunrise(sunriseData.sunrise);
        setSunset(sunriseData.sunset);
        setSolarNoon(sunriseData.solarNoon);

        // Update last fetch reference
        lastFetchRef.current = {
          lat,
          lng,
          date: selectedDate.toDateString(),
        };
      } else {
        setError(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
        );
      }
    } catch (error) {
      // Only set error if not an abort error
      if (error instanceof Error && error.name !== "AbortError") {
        console.error(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui:",
          error,
        );
        setError(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
        );
      }
    } finally {
      // Only update state if component is still mounted
      if (abortControllerRef.current !== null) {
        setLoading(false);
      }
    }
  };

  const fetchDefaultLocationData = async () => {
    if (loading) return; // Avoid duplicate requests

    // Cancel any in-progress fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    // Set the submitted location to the default location name
    setSubmittedLocation(defaultLocationName);

    try {
      // Use the first default location's coordinates
      const lat = defaultLocations[0].lat ?? 44.4268; // Default București coordinates
      const lng = defaultLocations[0].lng ?? 26.1025;

      // Skip if we've already fetched this data
      if (shouldSkipFetch(lat, lng, selectedDate)) {
        setLoading(false);
        return;
      }

      const sunriseData = await fetchSunriseSunsetData(
        lat,
        lng,
        selectedDate,
        dataSource,
      );

      // Check if request was aborted or component unmounted
      if (abortControllerRef.current === null) return;

      if (sunriseData) {
        setSunrise(sunriseData.sunrise);
        setSunset(sunriseData.sunset);
        setSolarNoon(sunriseData.solarNoon);

        // Update last fetch reference
        lastFetchRef.current = {
          lat,
          lng,
          date: selectedDate.toDateString(),
        };
      } else {
        setError(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
        );
      }
    } catch (error) {
      // Only set error if not an abort error
      if (error instanceof Error && error.name !== "AbortError") {
        console.error(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui:",
          error,
        );
        setError(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
        );
      }
    } finally {
      // Only update state if component is still mounted
      if (abortControllerRef.current !== null) {
        setLoading(false);
      }
    }
  };

  const handleSuggestionSelect = useCallback(
    async (suggestion: GeocodingSuggestion) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      setSuggestions([]);
      setLoading(true);
      setError(null);
      setSubmittedLocation(suggestion.display_name);
      const oldSunrise = sunrise,
        oldSunset = sunset,
        oldSolarNoon = solarNoon;
      try {
        const data = await fetchSunriseSunsetData(
          suggestion.lat,
          suggestion.lng,
          selectedDate,
          dataSource,
        );
        if (abortControllerRef.current === null) return;
        if (data) {
          setSunrise(data.sunrise);
          setSunset(data.sunset);
          setSolarNoon(data.solarNoon);
          lastFetchRef.current = {
            lat: suggestion.lat,
            lng: suggestion.lng,
            date: selectedDate.toDateString(),
          };

          // Save the location to localStorage for future visits
          saveLocation(
            location,
            suggestion.display_name,
            suggestion.lat,
            suggestion.lng,
          );

          // Show save shortcut button if user searched for a location
          // Check both state flag and ref flag
          const wasUserInitiated = isUserInitiatedSearch || userRequestedRef.current;
          if (wasUserInitiated && canAddMoreShortcuts()) {
            setShowSaveShortcutButton(true);
          }
          // Reset the flags after use
          setIsUserInitiatedSearch(false);
          userRequestedRef.current = false;
        } else {
          setError(
            "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
          );
          setSunrise(oldSunrise);
          setSunset(oldSunset);
          setSolarNoon(oldSolarNoon);
        }
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") {
          console.error(err);
          setError(
            "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
          );
          setSunrise(oldSunrise);
          setSunset(oldSunset);
          setSolarNoon(oldSolarNoon);
        }
      } finally {
        if (abortControllerRef.current !== null) setLoading(false);
      }
    },
    [selectedDate, dataSource, sunrise, sunset, solarNoon, location],
  );

  const fetchSunriseSunset = useCallback(async () => {
    if (loading) return;
    setSuggestions([]);
    // Cancel any in-progress fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Store the user request flag value before we start async operations
    const isUserRequested = userRequestedRef.current;

    // Automatic fetch on date-change: reuse last fetched coords
    if (!isUserRequested) {
      // Determine coords: last fetch or default
      let lat: number, lng: number;
      if (lastFetchRef.current) {
        ({ lat, lng } = lastFetchRef.current);
      } else {
        lat = defaultLocations[0].lat ?? 44.4268;
        lng = defaultLocations[0].lng ?? 26.1025;
      }
      setLoading(true);
      setError(null);
      try {
        const sunriseData = await fetchSunriseSunsetData(
          lat,
          lng,
          selectedDate,
          dataSource,
        );
        if (!abortControllerRef.current) return;
        if (sunriseData) {
          setSunrise(sunriseData.sunrise);
          setSunset(sunriseData.sunset);
          setSolarNoon(sunriseData.solarNoon);
          lastFetchRef.current = {
            lat,
            lng,
            date: selectedDate.toDateString(),
          };
        } else {
          setError(
            "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
          );
        }
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error(error);
          setError(
            "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
          );
        }
      } finally {
        if (abortControllerRef.current) setLoading(false);
      }
      return;
    }
    // User-initiated fetch: suggestion flow
    setSuggestionLoading(true);
    let geoSuggestions: GeocodingSuggestion[];
    try {
      geoSuggestions = await geocodeLocationSuggestions(location);
    } finally {
      setSuggestionLoading(false);
    }
    if (geoSuggestions.length > 1) {
      setSuggestions(geoSuggestions);
      return;
    }
    if (geoSuggestions.length === 0) {
      setError("Nu s-au putut găsi coordonate pentru locația furnizată");
      return;
    }
    const { lat, lng, display_name } = geoSuggestions[0];

    setLoading(true);
    setError(null);
    setSubmittedLocation(display_name);
    const oldSunrise = sunrise;
    const oldSunset = sunset;
    const oldSolarNoon = solarNoon;

    try {
      // Skip if we've already fetched this data for same coords and date
      if (shouldSkipFetch(lat, lng, selectedDate)) {
        setLoading(false);
        return;
      }

      // Pass the selected date to the API
      const sunriseData = await fetchSunriseSunsetData(
        lat,
        lng,
        selectedDate,
        dataSource,
      );

      // Check if request was aborted or component unmounted
      if (abortControllerRef.current === null) return;

      if (sunriseData) {
        setSunrise(sunriseData.sunrise);
        setSunset(sunriseData.sunset);
        setSolarNoon(sunriseData.solarNoon);

        // Update last fetch reference
        lastFetchRef.current = {
          lat,
          lng,
          date: selectedDate.toDateString(),
        };

        // Save the location to localStorage for future visits
        saveLocation(location, display_name, lat, lng);

        // Show save shortcut button if user searched for a location
        if (isUserRequested && canAddMoreShortcuts()) {
          setShowSaveShortcutButton(true);
        }
        // Reset the flag after use
        userRequestedRef.current = false;
      } else {
        setError(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
        );
        setSunrise(oldSunrise);
        setSunset(oldSunset);
        setSolarNoon(oldSolarNoon);
      }
    } catch (error) {
      // Only set error if not an abort error
      if (error instanceof Error && error.name !== "AbortError") {
        console.error(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui:",
          error,
        );
        setError(
          "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
        );
        setSunrise(oldSunrise);
        setSunset(oldSunset);
        setSolarNoon(oldSolarNoon);
      }
    } finally {
      // Only update state if component is still mounted
      if (abortControllerRef.current !== null) {
        setLoading(false);
      }
    }
  }, [
    loading,
    location,
    selectedDate,
    defaultLocations,
    sunrise,
    sunset,
    solarNoon,
    shouldSkipFetch,
    dataSource,
  ]);

  // Wrap user fetch to set flag
  const handleUserFetch = useCallback(() => {
    userRequestedRef.current = true;
    setIsUserInitiatedSearch(true);
    fetchSunriseSunset();
  }, [fetchSunriseSunset]);

  const handleLocationChange = useCallback((newLocation: string) => {
    setLocation(newLocation);
    setSuggestions([]); // clear previous suggestions
  }, []);

  const handleDateChange = useCallback((newDate: Date) => {
    setSelectedDate(newDate);
  }, []);

  const fetchSunriseSunsetForLocation = useCallback(
    async (locationName: string) => {
      if (loading) return; // Prevent duplicate requests

      // Cancel any in-progress fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      // Clear the data, but keep a reference to maintain the placeholder space
      const oldSunrise = sunrise;
      const oldSunset = sunset;
      const oldSolarNoon = solarNoon;

      try {
        const geoResult = await geocodeLocation(locationName);

        // Check if request was aborted or component unmounted
        if (abortControllerRef.current === null) return;

        if (geoResult) {
          const { lat, lng } = geoResult;

          // Skip if we've already fetched this data
          if (shouldSkipFetch(lat, lng, selectedDate)) {
            setLoading(false);
            return;
          }

          const sunriseData = await fetchSunriseSunsetData(
            lat,
            lng,
            selectedDate,
            dataSource,
          );

          // Check if request was aborted or component unmounted
          if (abortControllerRef.current === null) return;

          if (sunriseData) {
            setSunrise(sunriseData.sunrise);
            setSunset(sunriseData.sunset);
            setSolarNoon(sunriseData.solarNoon);

            // Update last fetch reference
            lastFetchRef.current = {
              lat,
              lng,
              date: selectedDate.toDateString(),
            };

            // Save the location to localStorage for future visits
            // Use the locationName as both the input and display name for default locations
            saveLocation(locationName, locationName, lat, lng);
          } else {
            setError(
              "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
            );
            // Restore old values if there's an error
            setSunrise(oldSunrise);
            setSunset(oldSunset);
            setSolarNoon(oldSolarNoon);
          }
        } else {
          setError("Nu s-au putut găsi coordonate pentru locația furnizată");
          // Keep old values if location not found
          setSunrise(oldSunrise);
          setSunset(oldSunset);
          setSolarNoon(oldSolarNoon);
        }
      } catch (error) {
        // Only set error if not an abort error
        if (error instanceof Error && error.name !== "AbortError") {
          console.error(
            "Eroare la obținerea momentelor de răsărit și apus ale Soarelui:",
            error,
          );
          setError(
            "Eroare la obținerea momentelor de răsărit și apus ale Soarelui",
          );
          // Restore old values if there's an error
          setSunrise(oldSunrise);
          setSunset(oldSunset);
          setSolarNoon(oldSolarNoon);
        }
      } finally {
        // Only update state if component is still mounted
        if (abortControllerRef.current !== null) {
          setLoading(false);
        }
      }
    },
    [
      loading,
      selectedDate,
      dataSource,
      sunrise,
      sunset,
      solarNoon,
      shouldSkipFetch,
    ],
  );

  const handleDefaultLocationClick = useCallback(
    (locationName: string) => {
      // Find the location in defaultLocations to get the apiDisplayName if available
      const locationData = defaultLocations.find(
        (loc) => loc.name === locationName,
      );
      const displayName = locationData?.apiDisplayName || locationName;

      setLocation(locationName); // Keep the short name in the input field
      setSubmittedLocation(displayName); // Use the API display name for the results
      setShowSaveShortcutButton(false); // Hide save button when clicking preset locations
      setIsUserInitiatedSearch(false); // Not a user search
      fetchSunriseSunsetForLocation(displayName); // Use the API display name for fetching
    },
    [defaultLocations, fetchSunriseSunsetForLocation],
  );

  const handleCustomShortcutClick = useCallback(
    async (shortcut: CustomShortcut) => {
      setLocation(shortcut.name);
      setSubmittedLocation(shortcut.submittedLocation);
      setShowSaveShortcutButton(false);
      setIsUserInitiatedSearch(false);

      // Use the saved coordinates directly instead of re-geocoding
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      setLoading(true);
      setError(null);

      try {
        const data = await fetchSunriseSunsetData(
          shortcut.lat,
          shortcut.lng,
          selectedDate,
          dataSource,
        );
        if (abortControllerRef.current === null) return;

        if (data) {
          setSunrise(data.sunrise);
          setSunset(data.sunset);
          setSolarNoon(data.solarNoon);
          lastFetchRef.current = {
            lat: shortcut.lat,
            lng: shortcut.lng,
            date: selectedDate.toDateString(),
          };
          saveLocation(shortcut.location, shortcut.submittedLocation, shortcut.lat, shortcut.lng);
        } else {
          setError("Eroare la obținerea momentelor de răsărit și apus ale Soarelui");
        }
      } catch (err: any) {
        if (err.name === "AbortError") return;
        setError("Eroare la obținerea momentelor de răsărit și apus ale Soarelui");
      } finally {
        setLoading(false);
      }
    },
    [selectedDate, dataSource],
  );

  const handleRemoveShortcut = useCallback(
    (name: string) => {
      handleRemoveCustomShortcut(name);
      // After removing, check if we should show the save button
      // Show it if:
      // 1. We have a location loaded
      // 2. There's room for more shortcuts
      // 3. The current location is NOT already saved (excluding the one we just deleted)
      if (submittedLocation && lastFetchRef.current && canAddMoreShortcuts()) {
        const updatedShortcuts = getCustomShortcuts();
        const isAlreadySaved = updatedShortcuts.some(
          (s) =>
            Math.abs(s.lat - lastFetchRef.current!.lat) < 0.001 &&
            Math.abs(s.lng - lastFetchRef.current!.lng) < 0.001,
        );
        if (!isAlreadySaved) {
          setShowSaveShortcutButton(true);
        }
      }
    },
    [handleRemoveCustomShortcut, submittedLocation],
  );

  const handleSaveAsShortcut = useCallback(() => {
    if (!submittedLocation || !lastFetchRef.current) return;

    // Extract a short name from the submitted location (first part before comma)
    const shortName = submittedLocation.split(",")[0].trim();

    const success = addCustomShortcut(
      shortName,
      location,
      submittedLocation,
      lastFetchRef.current.lat,
      lastFetchRef.current.lng,
    );

    if (success) {
      refreshCustomShortcuts();
      setShowSaveShortcutButton(false);
    }
  }, [submittedLocation, location, refreshCustomShortcuts]);

  // Handle timezone change
  const handleTimezoneChange = useCallback((timezone: string) => {
    setSelectedTimezone(timezone);
  }, []);

  // Memoize the SunriseResults component to prevent unnecessary re-renders
  const memoizedSunriseResults = useMemo(
    () => (
      <SunriseResults
        loading={loading}
        location={submittedLocation}
        defaultLocationName={defaultLocationName}
        sunrise={sunrise}
        sunset={sunset}
        solarNoon={solarNoon}
        gulikaInterval={gulikaInterval}
        rahuInterval={rahuInterval}
        yamagandamInterval={yamagandamInterval}
        brahmaMuhurta={brahmaMuhurta}
        abhijitMuhurta={abhijitMuhurta}
        currentPlanetaryHour={currentPlanetaryHour}
        countdown={countdown}
        progress={progress}
        error={error}
        selectedDate={selectedDate}
        dataSource={dataSource}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={handleTimezoneChange}
      />
    ),
    [
      loading,
      submittedLocation,
      defaultLocationName,
      sunrise,
      sunset,
      solarNoon,
      gulikaInterval,
      rahuInterval,
      yamagandamInterval,
      brahmaMuhurta,
      abhijitMuhurta,
      currentPlanetaryHour,
      countdown,
      progress,
      error,
      selectedDate,
      dataSource,
      selectedTimezone,
      handleTimezoneChange,
    ],
  );

  return (
    <div className="sunrise-sunset-container">
      <h1>Momente esențiale ale zilei</h1>

      <DefaultLocations
        locations={defaultLocations}
        customShortcuts={customShortcuts}
        onLocationSelect={handleDefaultLocationClick}
        onCustomShortcutSelect={handleCustomShortcutClick}
        onRemoveCustomShortcut={handleRemoveShortcut}
        showSaveButton={showSaveShortcutButton}
        onSaveShortcut={handleSaveAsShortcut}
      />

      <LocationInput
        location={location}
        loading={loading}
        suggestionLoading={suggestionLoading}
        suggestions={suggestions}
        onLocationChange={handleLocationChange}
        onFetchData={handleUserFetch}
        onSuggestionSelect={handleSuggestionSelect}
        onClearSuggestions={() => setSuggestions([])}
      />

      <DateSelector
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        disabled={loading}
      />

      {error && <div className="error-message">{error}</div>}

      {memoizedSunriseResults}
    </div>
  );
};

export default React.memo(SunriseSunset);
